import { Box, LinearProgress } from "@mui/material";
import EvStationOutlinedIcon from "@mui/icons-material/EvStationOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { useEffect } from "react";
import ChargePointDetailsStepComponent from "./ChargePointDetailsStepComponent";
import PersonalDetailsStepComponent from "./PersonalDetailsStepComponent";
import PaymentDetailsStepComponent from "./PaymentDetailsStepComponent";
import ContactValidationStepComponent from "./ContactValidationStepComponent";
import { useParams, useSearchParams } from "react-router-dom";
import queryParamsHelper from "../../../utils/helpers/queryParams.helper";
import StartChargingTransactionStepComponent from "./StartChargingTransactionStepComponent";

import "../../../styles/components/chargePoint/wizardOneStepLayout.css";

const STEP_TITLES = [
  "פרטי עמדת טעינה",
  "פרטים אישיים",
  "אימות אמצעי תקשורת",
  "אמצעי תשלום",
  "תחילת טעינה",
];

const STEP_SUBTITLES = [
  "בדוק את פרטי העמדה והתעריף",
  "הזן פרטים כדי להמשיך",
  "אמת את מספר הטלפון/אימייל",
  "בחר אמצעי תשלום מאובטח",
  "אשר והתחל טעינה",
];

const TOTAL_STEPS = STEP_TITLES.length;

const STEP_ICONS = [
  <EvStationOutlinedIcon key="0" sx={{ fontSize: 21 }} />,
  <PersonOutlinedIcon key="1" sx={{ fontSize: 21 }} />,
  <VerifiedUserOutlinedIcon key="2" sx={{ fontSize: 21 }} />,
  <CreditCardOutlinedIcon key="3" sx={{ fontSize: 21 }} />,
  <PlayCircleOutlineIcon key="4" sx={{ fontSize: 21 }} />,
];

const setpIds = {
  chagePointDetails: 0,
  personalDetails: 1,
  validatePersonalDetails: 2,
  paymentDetails: 3,
  startChargingTransaction: 4,
};

function WizardIconStepper({ activeStep, totalSteps, stepTitles, stepIcons, onStepClick }) {
  return (
    <div className="wizardIconStepper" aria-label="שלבי התהליך">
      {stepIcons.map((icon, index) => {
        const isDone = index < activeStep;
        const isActive = index === activeStep;
        let stepClass = "wizardIconStep wizardIconStepTodo";
        if (isDone) stepClass = "wizardIconStep wizardIconStepDone";
        else if (isActive) stepClass = "wizardIconStep wizardIconStepActive";

        const showConnectorAfter = index < totalSteps - 1;
        const connectorDone = index < activeStep;

        return (
          <div key={index} className="wizardIconStepperSegment">
            <div
              className={stepClass}
              role={isDone ? "button" : "presentation"}
              onClick={isDone ? () => onStepClick(index) : undefined}
              onKeyDown={
                isDone
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onStepClick(index);
                      }
                    }
                  : undefined
              }
              tabIndex={isDone ? 0 : -1}
              aria-label={isDone ? `חזור לשלב ${index + 1}: ${stepTitles[index]}` : undefined}
            >
              {icon}
            </div>
            {showConnectorAfter && (
              <div
                className={`wizardIconConnector ${connectorDone ? "wizardIconConnectorDone" : "wizardIconConnectorTodo"}`}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export const StartChargingTransactionProcessSteps = ({ chargePoint }) => {
  const { companyId, chargePointId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const stepFromQuery = queryParamsHelper.get(searchParams, "step", "0");
  const activeStep = Math.min(Math.max(parseInt(stepFromQuery, 10) || 0, 0), TOTAL_STEPS - 1);

  const updateStep = (step) => {
    const newParams = queryParamsHelper.set(searchParams, "step", String(step));
    setSearchParams(newParams, { replace: true });
  };

  useEffect(() => {
    const paymentTokenId = queryParamsHelper.get(searchParams, "paymentTokenId");
    if (paymentTokenId && activeStep !== setpIds.startChargingTransaction) {
      updateStep(setpIds.startChargingTransaction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleNext = () => {
    updateStep(activeStep + 1);
  };

  const handleBack = () => {
    updateStep(Math.max(0, activeStep - 1));
  };

  const handleReset = () => {
    const newParams = queryParamsHelper.removeMultiple(searchParams, ["step", "clientId", "paymentTokenId"]);
    setSearchParams(newParams, { replace: true });
  };

  const handleIconStepClick = (index) => {
    if (index < activeStep) updateStep(index);
  };

  const progressValue = ((activeStep + 1) / TOTAL_STEPS) * 100;
  const currentStepTitle = STEP_TITLES[activeStep] ?? STEP_TITLES[0];
  const currentStepSubtitle = STEP_SUBTITLES[activeStep] ?? STEP_SUBTITLES[0];

  const stepViews = [
    <ChargePointDetailsStepComponent key="step0" chargePoint={chargePoint} handleNext={handleNext} handleBack={handleBack} />,
    <PersonalDetailsStepComponent key="step1" handleNext={handleNext} handleBack={handleBack} handleReset={handleReset} />,
    <ContactValidationStepComponent key="step2" handleNext={handleNext} handleReset={handleReset} />,
    <PaymentDetailsStepComponent key="step3" chargePoint={chargePoint} handleNext={handleNext} handleBack={handleBack} />,
    <StartChargingTransactionStepComponent key="step4" chargePoint={chargePoint} handleNext={handleNext} handleBack={handleBack} />,
  ];
  const currentStepView = stepViews[activeStep] ?? stepViews[0];

  return (
    <Box sx={{ direction: "rtl", overflowY: "auto" }}>
      <div className="wizardOneStepContainer">
        <header className="wizardHeader">
          <WizardIconStepper
            activeStep={activeStep}
            totalSteps={TOTAL_STEPS}
            stepTitles={STEP_TITLES}
            stepIcons={STEP_ICONS}
            onStepClick={handleIconStepClick}
          />
          <p className="wizardProgressText" aria-live="polite">
            שלב {activeStep + 1} מתוך {TOTAL_STEPS}
          </p>
          <h2 className="wizardStepHeading">{currentStepTitle}</h2>
          <p className="wizardStepSubheading">{currentStepSubtitle}</p>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            className="wizardProgressBar"
            sx={{
              height: 4,
              borderRadius: 999,
              backgroundColor: "var(--surfaceAlt)",
              "& .MuiLinearProgress-bar": { backgroundColor: "var(--primaryColor)" },
            }}
          />
        </header>

        <div className="wizardCard">
          <div className="wizardCardContent">{currentStepView}</div>
        </div>
      </div>
    </Box>
  );
};

export default StartChargingTransactionProcessSteps;
